import { useEffect, useState } from 'react';
import {
  Download,
  ExternalLink,
  AlertCircle,
  RotateCcw,
  Captions,
  CaptionsOff,
  Undo2,
  Wrench,
} from 'lucide-react';
import type { Campaign } from '../../../shared/types';
import { ACTIVE_STAGES } from '../../../shared/types';
import {
  startCampaign,
  reviewCounts,
  reviseCampaign,
  errorText,
  type StartKind,
} from '../lib/api';
import { confirmDialog } from './ConfirmDialog';

type Variant = 'captioned' | 'plain';

/**
 * The finished video, or progress while it's being made.
 *
 * Both cuts are offered. v1 rendered and uploaded both files too, but only ever
 * wrote the captioned link back to the sheet — the plain version existed in
 * Drive with nothing in the app pointing at it.
 */
export default function VideoPlayer({
  campaign,
  onOpenCampaign,
}: {
  campaign: Campaign;
  /** Navigates the workspace to another campaign (the revision just created). */
  onOpenCampaign?: (id: string) => void;
}) {
  const hasCaptioned = Boolean(campaign.final_video_url);
  const hasPlain = Boolean(campaign.final_video_url_uncaptioned);

  const [variant, setVariant] = useState<Variant>(hasCaptioned ? 'captioned' : 'plain');
  const [restarting, setRestarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [revising, setRevising] = useState(false);

  // "One scene is wrong." The finished campaign is never rewound — it is the
  // record of what was delivered. A revision is a new campaign cloned from it
  // that opens straight at video review with every clip pre-approved; the
  // person flips only the wrong scene(s) to re-render and submits.
  const revise = async () => {
    const sceneCount = campaign.scenes?.length ?? 0;
    const ok = await confirmDialog({
      title: 'Fix a scene?',
      message:
        `A new campaign (a revision of #${campaign.seq}) is created with all ${sceneCount} scene(s) ` +
        `and their current clips carried over and pre-approved. It opens at video review: mark the ` +
        `scene(s) you want re-done as "Re-render" or "Re-render image", then submit. Only those ` +
        `scenes are generated again, and a fresh final video is assembled.\n\n` +
        `This campaign and its video stay exactly as they are. Nothing is billed until you submit ` +
        `re-render decisions on the revision.`,
      confirmLabel: 'Create revision',
    });
    if (!ok) return;
    setRevising(true);
    setError(null);
    try {
      const result = await reviseCampaign(campaign.id);
      onOpenCampaign?.(result.campaign);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setRevising(false);
    }
  };

  // If only one cut exists (a run that finished without captions), don't leave
  // the toggle pointing at a missing file.
  useEffect(() => {
    if (variant === 'captioned' && !hasCaptioned && hasPlain) setVariant('plain');
    if (variant === 'plain' && !hasPlain && hasCaptioned) setVariant('captioned');
  }, [variant, hasCaptioned, hasPlain]);

  // A load failure applies to one cut's URL, not the other's.
  useEffect(() => {
    setLoadFailed(false);
  }, [variant]);

  const working = ACTIVE_STAGES.includes(campaign.stage);
  const broken =
    campaign.stage === 'failed' ||
    campaign.stage === 'cancelled' ||
    campaign.stage === 'needs_attention';

  // What already exists, so the recovery options can offer the FREE paths
  // (back to review with the media already paid for) before the expensive one.
  const [counts, setCounts] = useState<{ images: number; videos: number } | null>(null);
  useEffect(() => {
    if (!broken) return;
    let cancelled = false;
    void reviewCounts(campaign.id)
      .then((c) => !cancelled && setCounts(c))
      .catch(() => !cancelled && setCounts({ images: 0, videos: 0 }));
    return () => {
      cancelled = true;
    };
  }, [broken, campaign.id]);

  if (working) {
    return (
      <div className="zen-card p-10 text-center space-y-4">
        <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-natural-bg">
          <span className="text-3xl">🎬</span>
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-natural-sage/40 animate-spin-slower" />
        </div>
        <div className="space-y-1.5">
          <p className="font-serif text-lg font-medium">
            {campaign.stage_message || 'Working on your video…'}
          </p>
          <p className="text-xs text-natural-text/55">
            You can close this page — the pipeline keeps running without it.
          </p>
        </div>
      </div>
    );
  }

  if (broken) {
    return (
      <div className="zen-card p-8 space-y-5">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-serif text-lg font-medium mb-1">
              {campaign.stage === 'cancelled'
                ? 'This run was cancelled'
                : campaign.stage === 'needs_attention'
                  ? 'This campaign needs a look'
                  : 'This run failed'}
            </p>
            <p className="text-xs text-natural-text/70 leading-relaxed break-words">
              {campaign.stage_message || 'No further detail was recorded.'}
            </p>
            <p className="text-[11px] text-natural-text/45 mt-2">
              The full step-by-step log is in the timeline above — including everything that ran
              before it stopped.
            </p>
          </div>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex flex-wrap gap-2">
          {(() => {
            const go = (kind: StartKind, confirmMessage?: string) => async () => {
              if (
                confirmMessage &&
                !(await confirmDialog({
                  title: 'Re-render everything?',
                  message: confirmMessage,
                  confirmLabel: 'Re-render all',
                  danger: true,
                }))
              ) {
                return;
              }
              setRestarting(true);
              setError(null);
              try {
                // A restart begins a new run, so the failed attempt's log stays intact.
                await startCampaign(campaign.id, kind);
              } catch (err) {
                setError(errorText(err));
              } finally {
                setRestarting(false);
              }
            };
            const sceneCount = campaign.scenes?.length ?? 0;

            // Nothing was ever rendered (the script step itself failed):
            // "re-render from scratch" is meaningless here. Re-running the
            // script is the only sensible move, and it costs next to nothing.
            if (counts && counts.images === 0 && counts.videos === 0) {
              return (
                <button
                  onClick={go('generate_script')}
                  disabled={restarting}
                  className="zen-btn-primary"
                  title="Runs the script step again — no images or videos are generated"
                >
                  <RotateCcw size={14} />
                  {restarting ? 'Working…' : 'Retry the script'}
                </button>
              );
            }

            return (
              <>
                {/* Free paths first: the media these lead back to is already paid for. */}
                {(counts?.videos ?? 0) > 0 && (
                  <button
                    onClick={go('resume_video_review')}
                    disabled={restarting}
                    className="zen-btn-primary"
                    title="Nothing is generated — the clips you already have come back for review"
                  >
                    <Undo2 size={14} />
                    Back to video review (free)
                  </button>
                )}
                {(counts?.images ?? 0) > 0 && (
                  <button
                    onClick={go('resume_image_review')}
                    disabled={restarting}
                    className={(counts?.videos ?? 0) > 0 ? 'zen-btn-ghost' : 'zen-btn-primary'}
                    title="Nothing is generated — the images you already have come back for review"
                  >
                    <Undo2 size={14} />
                    Back to image review (free)
                  </button>
                )}
                <button
                  onClick={go(
                    'render_images',
                    `This will generate ${sceneCount} new image(s), and after review ${sceneCount} new video clip(s). The images and clips you already have will be replaced.`
                  )}
                  disabled={restarting}
                  className="zen-btn-ghost"
                >
                  <RotateCcw size={14} />
                  {restarting ? 'Working…' : 'Re-render from scratch'}
                </button>
              </>
            );
          })()}
        </div>

        {(counts?.images ?? 0) > 0 && (
          <p className="text-[11px] text-natural-text/45">
            "Back to review" reuses what was already generated — nothing is billed until you
            submit new re-render decisions.
          </p>
        )}
      </div>
    );
  }

  if (!hasCaptioned && !hasPlain) {
    return (
      <div className="zen-card p-10 text-center text-xs text-natural-text/50">
        No video has been produced for this campaign yet.
      </div>
    );
  }

  const url = variant === 'captioned' ? campaign.final_video_url : campaign.final_video_url_uncaptioned;
  const fileId =
    variant === 'captioned' ? campaign.final_video_id : campaign.final_video_id_uncaptioned;
  const portrait = campaign.aspect_ratio === '9:16';

  return (
    <div className="zen-card p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-serif text-lg font-medium">Final video</h3>

        {hasCaptioned && hasPlain && (
          <div className="flex gap-1 p-1 bg-stone-100/80 rounded-xl">
            <button
              onClick={() => setVariant('captioned')}
              className={`flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                variant === 'captioned' ? 'bg-white shadow-xs' : 'text-natural-text/60 hover:text-natural-text/90'
              }`}
            >
              <Captions size={13} />
              With captions
            </button>
            <button
              onClick={() => setVariant('plain')}
              className={`flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                variant === 'plain' ? 'bg-white shadow-xs' : 'text-natural-text/60 hover:text-natural-text/90'
              }`}
            >
              <CaptionsOff size={13} />
              No captions
            </button>
          </div>
        )}
      </div>

      {loadFailed ? (
        fileId ? (
          // Drive's direct-download links often refuse to stream inside a bare
          // <video> element (redirects, session checks, virus-scan pages for
          // big files). Drive's own embedded player has none of those problems,
          // so it is the automatic fallback — the video still plays in-page.
          <div className="flex justify-center bg-stone-900 rounded-2xl overflow-hidden">
            <iframe
              key={`${variant}-preview`}
              src={`https://drive.google.com/file/d/${fileId}/preview`}
              allow="autoplay; fullscreen"
              allowFullScreen
              title="Final video (Google Drive player)"
              className={portrait ? 'h-[70vh] aspect-[9/16] border-0' : 'w-full aspect-video border-0'}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 bg-stone-900 rounded-2xl py-16 px-6 text-center">
            <AlertCircle size={22} className="text-stone-500" />
            <p className="text-sm font-medium text-stone-300">Video unavailable</p>
            <p className="text-[11px] text-stone-500 max-w-xs leading-relaxed">
              The file won't stream here and no Drive copy is linked for this cut.
            </p>
          </div>
        )
      ) : (
        <div className="flex justify-center bg-stone-900 rounded-2xl overflow-hidden">
          <video
            // Remounts on switch so the browser loads the other file rather than
            // keeping the first one buffered.
            key={variant}
            src={url}
            controls
            playsInline
            preload="metadata"
            onError={() => setLoadFailed(true)}
            className={portrait ? 'max-h-[70vh] w-auto' : 'w-full h-auto'}
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <a href={url} download className="zen-btn-primary">
          <Download size={14} />
          Download {variant === 'captioned' ? 'captioned' : 'plain'}
        </a>

        {hasCaptioned && hasPlain && (
          <a
            href={
              variant === 'captioned'
                ? campaign.final_video_url_uncaptioned
                : campaign.final_video_url
            }
            download
            className="zen-btn-ghost"
          >
            <Download size={14} />
            Download {variant === 'captioned' ? 'plain' : 'captioned'}
          </a>
        )}

        {fileId && (
          <a
            href={`https://drive.google.com/file/d/${fileId}/view`}
            target="_blank"
            rel="noreferrer"
            className="zen-btn-ghost"
          >
            <ExternalLink size={14} />
            Open in Drive
          </a>
        )}

        {campaign.stage === 'done' && (
          <button
            onClick={() => void revise()}
            disabled={revising}
            className="zen-btn-ghost ml-auto"
            title="Start a revision: a new campaign at video review with these clips carried over, so you can re-render just the scene(s) that are wrong"
          >
            <Wrench size={14} />
            {revising ? 'Creating revision…' : 'Fix a scene'}
          </button>
        )}
      </div>

      {campaign.stage === 'done' && (
        <p className="text-[11px] text-natural-text/45">
          Something off in one scene? <strong>Fix a scene</strong> opens a new revision campaign
          where you re-render only that scene — this video stays as it is.
        </p>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      {(!hasCaptioned || !hasPlain) && (
        <p className="text-[11px] text-natural-text/45">
          {hasCaptioned
            ? 'Only the captioned cut is available for this campaign.'
            : 'Captions were not produced for this run — only the plain cut is available.'}
        </p>
      )}
    </div>
  );
}
